import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { z } from 'zod';
import useHandleAppException, {
  AppExceptionResponse,
} from '../../../../../hooks/useHandleAppException';
import LangEnum from '../../../../../lang/LangEnum';
import PATHS from '../../../../../routes/routes.const';
import api from '../../../../../services/api';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { MIN_PASSWORD_LENGTH } from '../../../../../utils/constants';
import { useTranslation } from '../../../../translation/TranslationContext';

const useResetPasswordForm = () => {
  const { translate } = useTranslation();
  const { handleErrorResponse } = useHandleAppException();
  const history = useHistory();
  const { search } = useLocation();
  const { handleMessage } = snackbarStore;

  const schema = z.object({
    password: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .min(
        MIN_PASSWORD_LENGTH,
        translate(LangEnum.LOGIN_PASSWORD_INFO, { min: MIN_PASSWORD_LENGTH }),
      ),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { password: '' },
    mode: 'onBlur',
  });

  const handleSubmit = form.handleSubmit(async ({ password }) => {
    const params = new URLSearchParams(search);
    const response = await api.resetPassword({ password, token: params.get('token') });

    if (response.ok) {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.SNACKBAR_PASSWORD_SUCCESSFULLY_RESET),
      });
      history.push(PATHS.public.login.path);
      return;
    }

    const errorMessage =
      (response.data
        ? handleErrorResponse(response as unknown as AppExceptionResponse)
        : undefined) || translate(LangEnum.SNACKBAR_INVALID_TOKEN);

    handleMessage({ type: 'error', message: errorMessage });
  });

  return { form, handleSubmit };
};

export default useResetPasswordForm;
